import { DatePickerSelectedValue } from '@storeblocks/datepicker';
import { format } from 'date-fns';
import { type FC } from 'react';
import styled from 'styled-components';

import { Show } from '@/components/Show';
import { DateSearch } from '@/components/Toolbar/DateSearch';
import { DownloadButton } from '@/components/Toolbar/DownloadButton';
import { DownloadErrorAlert } from '@/components/Toolbar/DownloadErrorAlert';
import { useSearchParams } from '@/components/Toolbar/hooks/useSearchParams';
import { TextSearch } from '@/components/Toolbar/TextSearch';
import { useHandleAsyncOperation } from '@/hooks/useHandleAsyncError';
import { useTexts } from '@/hooks/useTexts';
import { media } from '@/styles/variables';
import { stripFilename } from '@/util/stripFilename';

import { useReportsContext } from '../context/ReportsProvider';
import { downloadReports } from '../utils/downloadReports';

export interface SearchParams {
  date?: string;
  query?: string;
}

interface Props {
  onSearchParamsChange: (params: SearchParams) => void;

  /**
   * Initialize the search field with the specified query.
   */
  initialQuery?: string;
}

export const FundReportsToolbar: FC<Props> = ({
  onSearchParamsChange,
  initialQuery,
}) => {
  const texts = useTexts();
  const { selectedReports } = useReportsContext();

  const searchParams = useSearchParams<SearchParams>(onSearchParamsChange, {});

  const [handleDownloadOnClick, , downloadError] = useHandleAsyncOperation(
    async () => {
      await downloadReports([...selectedReports.values()]);
    },
  );

  const handleTextSearchChanged = (query: string): void => {
    searchParams.update((state) => {
      state.query = query;
    });
  };

  const handleDateSearchChanged = ({ date }: DatePickerSelectedValue): void => {
    const dateString = date ? format(date, 'yyyy-MM-dd') : '';

    searchParams.update((state) => {
      state.date = dateString;
    });
  };

  return (
    <>
      <Wrapper>
        <TextSearch
          onChange={handleTextSearchChanged}
          label={texts.reportsArchivePage.search}
          initialQuery={initialQuery}
        />

        <DateSearch
          onChange={handleDateSearchChanged}
          label={texts.reportsArchivePage.tableHeaders.reportDate}
        />

        <DownloadButton
          onClick={handleDownloadOnClick}
          numberOfFilesToDownload={selectedReports.size}
          data-trackid-downloaded-files={[...selectedReports.values()].map(
            (report) => stripFilename(report.name),
          )}
        />
      </Wrapper>

      <Show when={downloadError}>
        <DownloadErrorAlert />
      </Show>
    </>
  );
};

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  // Rows containing input fields must have a minimum height of 110px to allow room for an error alert
  // to be displayed below the input fields.
  grid-template-rows: minmax(110px, auto);
  gap: 16px;

  & > button {
    // Align the download button with the input fields
    margin-top: 30px;
  }

  @media ${media.tabletAndBelow} {
    grid-template-columns: 1fr;
    // Rows containing input fields must have a minimum height of 110px to allow room for an error alert
    // to be displayed below the input fields.
    grid-template-rows: repeat(2, minmax(110px, auto)) auto;

    & > button {
      margin-top: 0;
      justify-self: start;
    }
  }
`;
